<script setup>
import { useEditor, EditorContent, BubbleMenu, FloatingMenu } from '@tiptap/vue-3';
import Placeholder from '@tiptap/extension-placeholder';
import StarterKit from '@tiptap/starter-kit';
import EditorButtons from './EditorButton.vue';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';

const props = defineProps({
    modelValue: [String, Object],
    placeholder: {
        type: String,
        default: 'Type something...',
    },
    imagable: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['update:modelValue', 'focus', 'blur']);

const editor = useEditor({
    extensions: [
        StarterKit,
        Image,
        Underline,
        Placeholder.configure({
            placeholder: () => {
                return props.placeholder;
            },
        }),
    ],
    content: props.modelValue || '',

    onUpdate: () => {
        if (!editor.value) return;

        emit('update:modelValue', editor.value.isEmpty ? null : editor.value.getJSON());
    },

    onFocus: () => {
        emit('focus');
    },

    onBlur: () => {
        emit('blur');
    },
});
</script>

<template>
    <div>
        <editor-content :editor="editor" />
        <div v-if="editor">
            <bubble-menu
                :tippy-options="{
                    duration: 100,
                    placement: 'bottom-start',
                    maxWidth: 'none',
                }"
                :editor="editor"
                class="bubble"
            >
                <EditorButtons :editor="editor" :imagable="props.imagable" />
            </bubble-menu>
            <floating-menu
                :tippy-options="{
                    duration: 100,
                    placement: 'bottom-start',
                    maxWidth: 'none',
                }"
                :editor="editor"
                class="float"
            >
                <EditorButtons :editor="editor" :imagable="props.imagable" />
            </floating-menu>
        </div>
    </div>
</template>
<style>
.editor-container {
    @apply relative;
}

.editor {
    @apply p-4;
}

.tiptap .is-empty:before {
    content: attr(data-placeholder);

    @apply h-0 pointer-events-none float-left text-gray-500;
}

.tiptap {
    @apply outline-none;
}

.tiptap:first-child {
    @apply mt-0;
}
.tiptap ul {
    list-style: circle;
}
.tiptap ol {
    list-style-type: decimal;
}
.tiptap ul,
.tiptap ol {
    @apply my-3;
    padding: 0 1rem;
}

.tiptap ul li p,
.tiptap ol li p {
    @apply py-0;
}
.tiptap p {
    @apply text-sm;
}

.tiptap h1,
.tiptap h2,
.tiptap h3,
.tiptap h4,
.tiptap h5,
.tiptap h6 {
    @apply leading-6 font-medium;
}

.tiptap h1 {
    @apply text-lg;
}

.tiptap h2 {
    @apply text-base;
}

.tiptap hr {
    @apply border-[none] border-t-[1px] border-gray-200 my-4;
}

.tiptap img {
    @apply w-[200px];
}
</style>
